import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'md' | 'lg' | 'xl' | 'full';
}

export function Modal({ open, onClose, title, children, size = 'lg' }: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const onOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  const isFull = size === 'full';

  const widthClass = size === 'md'
    ? 'max-w-md max-h-[90vh] sm:max-h-[85vh] sm:rounded-xl sm:m-4'
    : size === 'xl'
      ? 'max-w-4xl max-h-[90vh] sm:max-h-[85vh] sm:rounded-xl sm:m-4'
      : isFull
        ? 'fixed inset-0 w-screen h-screen max-w-full max-h-full rounded-none m-0 p-0 border-none'
        : 'max-w-2xl max-h-[90vh] sm:max-h-[85vh] sm:rounded-xl sm:m-4';

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            ref={overlayRef}
            className={`fixed inset-0 bg-black/50 z-50 flex items-center justify-center ${isFull ? 'p-0 m-0' : ''}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onOverlayClick}
          >
            <motion.div
              className={`card w-full ${widthClass} flex flex-col`}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-sm sm:text-base font-semibold text-gray-900">{title}</h2>
                <button onClick={onClose}
                  className="rounded p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 sm:p-5">
                {children}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
